import { useFormikContext } from "formik";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CATEGORY_TYPES } from "../../../../constants/widget-constants";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { ListQuery } from "../../../../models/ListQuery";
import { Name } from "../../../../models/Name";
import { getAllAccessGroups } from "../../../../services/accessGroupService";
import { getCompanyById } from "../../../../services/companyService";
import { getCredentialsTypeWithPagination } from "../../../../services/credentialsService";
import { getDepartmentById } from "../../../../services/departmentService";
import { getErrorMessage } from "../../../../services/utilities/ServiceError";
import { getStatusName } from "../../../../utilities/CredentialUtil";
import { appendFilter } from "../../../../utilities/filters";
import ActivityFrame from "../../../activity-frame/activity-frame";
import CredentialDateInfo from "../../../enroll-credentials/credential-date-info";

interface SummaryProps {
    onBack: (event: any) => void;
    setSideBarStepDescription: (discription: string) => void;
    credentialType: string;
    taskProcedure: string;
}

type CustomEntry = {
    displayName: string;
    value: string;
};

const QuickEnrolSummary: FC<SummaryProps> = ({ onBack, setSideBarStepDescription, credentialType, taskProcedure }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [ accessGroupsData, setAccessGroupsData] = useState<any>([]);
    const [ companyName, setCompanyName] = useState<any>([]);
    const [ departmentName, setDepartmentName] = useState<any>([]);
    const [busy, setBusy] = useState(false);
    const customEntries = values.custom ? Object.values(values.custom) as CustomEntry[]: undefined;
    const [credentialTypes, setCredentialTypes] = useState<Name[]>([]);
    
    useEffect(() => {
        setBusy(true);
        getData().finally(() => setBusy(false));
        setSideBarStepDescription("");
    }, []);

    const fetchAccessGroupsData = async (ids: string[], listParameters?: ListQuery) => {
        const data = await getAllAccessGroups(
            null,
            translate,
            {
                ...listParameters,
                filters: appendFilter(
                    ids.toString(),
                    "AccessGroups",
                    "id",
                    listParameters?.filters ?? [],
                    "=in=")
            }
        );

        return data.rows;
    };

    const getData = async () => {
        try {
            const promises: { key: string, promise: Promise<any> }[] = [];
            
            if (values.assignedAccessGroups && values.assignedAccessGroups.length > 0) {
                promises.push({ key: 'accessGroups', promise: fetchAccessGroupsData(values.assignedAccessGroups) });
            }
            
            if (values.company) {
                promises.push({ key: 'company', promise: getCompanyById(values.company) });
            }
            
            if (values.department) {
                promises.push({ key: 'department', promise: getDepartmentById(values.department) });
            }
            
            const results = await Promise.all(promises.map(p => p.promise));
            
            results.forEach((result, index) => {
                switch (promises[index].key) {
                    case 'accessGroups':
                        setAccessGroupsData(result);
                        break;
                    case 'company':
                        setCompanyName(result.name);
                        break;
                    case 'department':
                        setDepartmentName(result.name);
                        break;
                }
            });
        } catch (error) {
            toast.error(translate.byKey(TranslationKeys.error));
        }
    }

    useEffect(() => {
        buildCredentialTypes();
    }, [])

    const buildCredentialTypes = async () => {
        try {
            const type = await getCredentialsTypeWithPagination();
            setCredentialTypes(type.rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }
    
    const getType = (typeId: number) => {
        let type: string = '';

        if (!typeId) {
            return type;
        }

        credentialTypes.forEach(item => {
            if (item.value === typeId.toString()) {
                type = item.label;
            }
        })

        return type;
    }

    const onLocalBack = (event: any) => {
        onBack({ ...event });
        setSideBarStepDescription(translate.byKey(TranslationKeys.pending))
    };

    return <>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white h-full'>
            {/* page title */}
            <h1 className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>
                {translate.byKey(TranslationKeys.summary)}
            </h1>

            <div style={{overflowY: "auto", height: "380px", overflowX: "hidden"}}>
                {/* category type */}
                <div className="flex pb-3">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.categoryType)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {values.categoryType}
                        </div>
                    </div>
                </div>
                <hr />

                {/* user details */}
                <>
                    <div className="flex pb-3 mt-1">
                        <div className="w-1/3">
                            <h4 className="p-1 text-xs text-gray-600">
                                {translate.byKey(TranslationKeys.userDetails)}:
                            </h4>
                        </div>
                        <div className="w-2/3">
                            {values.title && 
                                <div className="mt-1">
                                    <div className="text-sm font-bold text-black">
                                        {translate.byKey(TranslationKeys.title)}
                                    </div>
                                    <div className="text-sm text-black">
                                        {values.title ? values.title : '-'}
                                    </div>
                                </div>
                            }

                            {values.firstName && 
                                <div className="mt-2">
                                    <div className="text-sm font-bold text-black">
                                        {translate.byKey(TranslationKeys.firstName)}
                                    </div>
                                    <div className="text-sm text-black">
                                        {values.firstName}
                                    </div>
                                </div>
                            }

                            {values.lastName && 
                                <div className="mt-2">
                                    <div className="text-sm font-bold text-black">
                                        {translate.byKey(TranslationKeys.lastName)}
                                    </div>
                                    <div className="text-sm text-black">
                                        {values.lastName}
                                    </div>
                                </div>
                            }

                            <div className="mt-2">
                                <div className="text-sm font-bold text-black">
                                    {values.categoryTypeSysname === CATEGORY_TYPES.VEHICLE
                                        ? translate.byKey(TranslationKeys.makeModel)
                                        : translate.byKey(TranslationKeys.displayName)
                                    }
                                </div>
                                <div className="text-sm text-black">
                                    {values.name}
                                </div>
                            </div>

                            <div className="mt-2">
                                <div className="text-sm font-bold text-black">
                                    {values.categoryTypeSysname === CATEGORY_TYPES.VEHICLE
                                        ? translate.byKey(TranslationKeys.RegistrationNr)
                                        : translate.byKey(TranslationKeys.idNumber)
                                    }
                                </div>
                                <div className="text-sm text-black">
                                    {values.uid}
                                </div>
                            </div>

                            {values.department &&
                                <div className="mt-2">
                                    <div className="text-sm font-bold text-black">
                                        {translate.byKey(TranslationKeys.department)}
                                    </div>
                                    <div className="text-sm text-black">
                                        {departmentName}
                                    </div>
                                </div>
                            }

                            {values.company &&
                                <div className="mt-2 mb-1">
                                    <div className="text-sm font-bold text-black">
                                        {translate.byKey(TranslationKeys.company)}
                                    </div>
                                    <div className="text-sm text-black">
                                        {companyName}
                                    </div>
                                </div>
                            }
                            
                            {customEntries && customEntries.map((entry, index) => (
                                <div key={index} className="mt-2 mb-1">
                                    <div className="text-sm font-bold text-black">
                                        {entry.displayName}
                                    </div>
                                    <div className="text-sm text-black">
                                        {entry.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <hr />
                </>

                {/* credential type */}
                { values.code && credentialType && taskProcedure && <>
                    {/* credential type */}
                    <div className="flex pb-3 mt-1">
                        <div className="w-1/3">
                            <h4 className="p-1 text-xs text-gray-600">
                                {translate.byKey(TranslationKeys.credentialType)}:
                            </h4>
                        </div>
                        <div className="w-2/3 mt-1">
                            <div className="text-sm text-black">
                                {credentialType}
                            </div>
                        </div>
                    </div>
                    <hr />

                    {/* task procedure */}
                    <div className="flex pb-3 mt-1">
                        <div className="w-1/3">
                            <h4 className="p-1 text-xs text-gray-600">
                                {translate.byKey(TranslationKeys.taskProcedure)}:
                            </h4>
                        </div>
                        <div className="w-2/3 mt-1">
                            <div className="text-sm text-black">
                                {taskProcedure}
                            </div>
                        </div>
                    </div>
                    <hr />

                    {/* card summary details */}
                    <div className="flex pb-3 mt-1">
                        <div className="w-1/3">
                            <h4 className="p-1 text-xs text-gray-600">{translate.byKey(TranslationKeys.enrolmentDetails)}:</h4>
                        </div>
                        <div className="w-2/3 mt-1">
                            <div className="text-sm text-black">
                                <h4 className="p-1 text-xs text-gray-600">{translate.byKey(TranslationKeys.tagCode)}: <span className="text-sm text-black">{values.code}</span></h4>
                                <h4 className="p-1 text-xs text-gray-600">{translate.byKey(TranslationKeys.type)}: <span className="text-sm text-black">{getType(values.typeId)}</span></h4>
                                <h4 className="p-1 text-xs text-gray-600">{translate.byKey(TranslationKeys.status)}: <span className="text-sm text-black">{getStatusName(values.statusId, translate)}</span></h4>
                                <CredentialDateInfo values={values}/>
                            </div>
                        </div>
                    </div>
                    <hr />
                </>
                }

                {/* access groups assigned */}
                {(values.accessGroups?.length > 0)  && 
                    <>
                        <div className="flex pb-3 mt-1">
                            <div className="w-1/3">
                                <h4 className="p-1 text-xs text-gray-600">
                                    {translate.byKey(TranslationKeys.accessGroups)}:
                                </h4>
                            </div>
                            <div className="w-2/3 mt-1">
                                {values.accessGroups.map((data: any) => {
                                    return (
                                        <div
                                            className="text-sm text-black"
                                            key={data.id}
                                        >
                                            {data.name}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <hr />
                    </>
                }

                {/*Floor Groups */}
                {values.floorGroups?.length > 0 && (
                    <>
                        <div className="flex pb-3 mt-1">
                            <div className="w-1/3">
                                <h4 className="p-1 text-xs text-gray-600">{translate.byKey(TranslationKeys.floorGroups)}:</h4>
                            </div>
                            <div className="w-2/3 mt-1">
                                {values.floorGroups.map((data: any) => (
                                    <div className="text-sm text-black" key={data.id}>{data.name}</div>
                                ))}
                            </div>
                        </div>
                        <hr />
                    </>
                )}
            </div>

            {/* Buttons */}
            <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
                <div className="flex flex-row mt-4">
                    <button
                        className="py-1 px-5 secondary secondary-inverse"
                        type="button" onClick={onLocalBack} >{translate.byKey(TranslationKeys.back)}</button>
                </div>

                <div className="flex flex-row justify-end mt-4 pl-3">
                    <button
                        className={`py-1 px-5 secondary secondary-enabled`}
                        type="submit">
                        {translate.byKey(TranslationKeys.confirm)}
                    </button>
                </div>
            </div>
        </ActivityFrame>
    </>
}

export default QuickEnrolSummary;